import { useMemo } from 'react'
import { cn } from '@/lib/utils'

export interface DualTimeDisplayProps {
  /** RFC3339/ISO UTC timestamp to render. */
  utcTimestamp: string
  /**
   * IANA timezone for the organization (e.g. "America/Toronto"). When the
   * viewer's device tz matches — or when this value is missing/invalid — the
   * component collapses to a single device-local line.
   */
  orgTimezone?: string
  /**
   * Optional server-rendered date string shown verbatim when `utcTimestamp`
   * is missing or unparseable. Pre-Wave-1 callers fell back to these
   * strings on parse failure; the dual-tz rewrite accidentally dropped
   * that behavior, so this prop restores it for callers that care.
   */
  fallbackDate?: string
  /** Companion to `fallbackDate`; rendered on its own line below. */
  fallbackTime?: string
  className?: string
}

interface Formatted {
  dateTime: string
  shortTzLabel?: string
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}

const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
}

function formatInZone(date: Date, timeZone?: string): Formatted {
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    ...DATE_OPTIONS,
    ...(timeZone ? { timeZone } : {}),
  })
  const timeFmt = new Intl.DateTimeFormat(undefined, {
    ...TIME_OPTIONS,
    ...(timeZone ? { timeZone } : {}),
  })
  const dateTime = `${dateFmt.format(date)}, ${timeFmt.format(date)}`

  let shortTzLabel: string | undefined
  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(date)
    shortTzLabel = parts.find((p) => p.type === 'timeZoneName')?.value
  }

  return { dateTime, shortTzLabel }
}

/**
 * Validate a timezone name by attempting to build a formatter with it. Returns
 * `true` if the zone is usable. `Intl.DateTimeFormat` throws `RangeError` on
 * invalid zones, so this must catch at *construction* time, not only formatting.
 */
function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/**
 * Renders a timestamp in the viewer's device tz and — when it differs — the
 * organization's configured tz too. Full date + time on each line disambiguates
 * cross-midnight shifts. Falls back to a single device-local line on missing /
 * invalid `orgTimezone`. On missing / invalid `utcTimestamp`, falls back to
 * the caller-provided `fallbackDate` + `fallbackTime` verbatim; if those are
 * also absent, renders nothing.
 */
export function DualTimeDisplay({
  utcTimestamp,
  orgTimezone,
  fallbackDate,
  fallbackTime,
  className,
}: DualTimeDisplayProps) {
  const content = useMemo(() => {
    const date = new Date(utcTimestamp)
    if (isNaN(date.getTime())) return null

    const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const orgTzValid =
      !!orgTimezone && orgTimezone !== deviceTz && isValidTimeZone(orgTimezone)

    if (!orgTzValid) {
      if (orgTimezone && orgTimezone !== deviceTz) {
        // Reached only when orgTimezone is non-empty, differs from device tz,
        // and is rejected by Intl. Warn once so ops can notice bad stored data.
        console.warn(
          `DualTimeDisplay: invalid orgTimezone "${orgTimezone}", falling back to device tz`
        )
      }
      const { dateTime } = formatInZone(date)
      return [{ dateTime, label: undefined, shortTzLabel: undefined }]
    }

    const device = formatInZone(date)
    const org = formatInZone(date, orgTimezone)
    return [
      { dateTime: device.dateTime, label: 'your time' },
      {
        dateTime: org.dateTime,
        label: org.shortTzLabel
          ? `building time, ${org.shortTzLabel}`
          : 'building time',
      },
    ]
  }, [utcTimestamp, orgTimezone])

  if (!content) {
    // Parse failure. If the caller provided server-rendered strings,
    // surface them so the page doesn't silently blank out. Otherwise
    // keep the prior behavior of rendering nothing.
    if (fallbackDate || fallbackTime) {
      return (
        <div
          className={cn('space-y-0.5', className)}
          data-testid="dual-time-fallback"
        >
          {fallbackDate && <div className="text-base">{fallbackDate}</div>}
          {fallbackTime && <div className="text-base">{fallbackTime}</div>}
        </div>
      )
    }
    return null
  }

  return (
    <div className={cn('space-y-0.5', className)}>
      {content.map((line, idx) => (
        <div key={idx} data-testid="dual-time-line" className="text-base">
          <span>{line.dateTime}</span>
          {line.label && (
            <span className="text-muted-foreground text-sm ml-1.5">
              ({line.label})
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
